import type { UIMessage } from 'ai';

import { asToolPart } from './tool-parts';

/**
 * The chat messages a `MemorySuggestionCard` sends, and the reverse lookup that
 * tells a reloaded transcript which suggestions were already answered.
 *
 * A card answers by sending a message (there is no tool-output channel back),
 * and its "Saving…" / hidden state used to live only in `useState`. Reloading
 * the conversation brought every answered card back with live Save/Dismiss
 * buttons, and a second Save wrote a duplicate memory. The transcript already
 * records the answer as the owner's reply, so the answered state is derived
 * from it here, with the builders and the parser in one module so the wording
 * cannot drift apart.
 */

export type MemorySuggestionAnswer = 'saved' | 'dismissed';

interface SuggestionReplyInput {
  content: string;
  scopeSkillIds?: string[];
}

const SAVE_PREFIX = 'Yes, save that as a memory';
const REPLACE_PREFIX = 'Replace the existing memory';
const DISMISS_PREFIX = "Don't save that as a memory";

const scopeSuffix = (scopeSkillIds?: string[]) =>
  scopeSkillIds && scopeSkillIds.length > 0
    ? ` (scoped to ${scopeSkillIds.join(', ')})`
    : '';

export const buildSaveMemoryReply = ({
  content,
  scopeSkillIds,
}: SuggestionReplyInput) =>
  `${SAVE_PREFIX} from the suggestion card${scopeSuffix(scopeSkillIds)}: "${content}"`;

export const buildReplaceMemoryReply = ({
  content,
  scopeSkillIds,
  conflictsWith,
}: SuggestionReplyInput & {
  conflictsWith: { knowledgeEntryId: string; content: string };
}) =>
  `${REPLACE_PREFIX} (id ${conflictsWith.knowledgeEntryId}, "${conflictsWith.content}") ` +
  `with this one as a memory from the suggestion card${scopeSuffix(scopeSkillIds)}: "${content}"`;

export const buildDismissMemoryReply = ({
  content,
  scopeSkillIds,
}: SuggestionReplyInput) =>
  `${DISMISS_PREFIX} from the suggestion card${scopeSuffix(scopeSkillIds)}: "${content}"`;

const answerFromReply = (
  text: string,
  content: string
): MemorySuggestionAnswer | null => {
  if (!text.endsWith(`: "${content}"`)) return null;
  if (text.startsWith(SAVE_PREFIX) || text.startsWith(REPLACE_PREFIX)) {
    return 'saved';
  }
  if (text.startsWith(DISMISS_PREFIX)) return 'dismissed';
  return null;
};

const userText = (message: UIMessage) =>
  message.parts
    .map((part) => (part.type === 'text' ? part.text : ''))
    .join('')
    .trim();

/**
 * Map each `memory_suggestion` card's tool call id to the answer a LATER user
 * message gave it. A card with no matching reply is absent (still open).
 */
export function getMemorySuggestionAnswers(
  messages: UIMessage[]
): Map<string, MemorySuggestionAnswer> {
  const answers = new Map<string, MemorySuggestionAnswer>();
  const open: { toolCallId: string; content: string }[] = [];

  for (const message of messages) {
    if (message.role === 'user') {
      const text = userText(message);
      for (const suggestion of open) {
        if (answers.has(suggestion.toolCallId)) continue;
        const answer = answerFromReply(text, suggestion.content);
        if (answer) answers.set(suggestion.toolCallId, answer);
      }
      continue;
    }

    for (const part of message.parts) {
      const toolPart = asToolPart(part);
      const presentation = (
        toolPart?.output as
          | { presentation?: { type?: string; content?: unknown } }
          | undefined
      )?.presentation;
      if (
        toolPart &&
        presentation?.type === 'memory_suggestion' &&
        typeof presentation.content === 'string'
      ) {
        open.push({
          toolCallId: toolPart.toolCallId,
          content: presentation.content,
        });
      }
    }
  }

  return answers;
}
